import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import SelectWithNewOption, { SelectOption } from '@/components/ui/SelectWithNewOption/SelectWithNewOption';
import { TAgent } from '@/types/agents';
import { TAgentConfig } from '@/types/configs';
import { TCredentialCreate, TSystemCredential } from '@/types/credentials';

import CreateCredentialForm from '../CreateNewCredentialModal/CreateCredentialForm';
import CustomTextField from '../CustomTextField/CustomTextField';
import Modal from '../Modal/Modal';

import useCreateNewConfigModal from './useCreateNewConfigModal';

export interface CreateNewConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: TAgent;
  agentConfigs: TAgentConfig[];
  credentialOptions: Record<string, SelectOption[]>;
  onSubmit: (data: { configName: string; selectedCredentials: Record<string, string> }) => void;
  onCreateCredential: (
    credential: TSystemCredential,
    data: TCredentialCreate
  ) => Promise<{ referenceId: string; customName: string; name: string }>;
  isLoading?: boolean;
  isCreatingCredential?: boolean;
}

const CreateNewConfigModal = ({
  isOpen,
  onClose,
  agent,
  agentConfigs,
  credentialOptions,
  onSubmit,
  onCreateCredential,
  isLoading = false,
  isCreatingCredential = false,
}: CreateNewConfigModalProps) => {
  const [credentialAddingFor, setCredentialAddingFor] = useState<{
    slotKey: string;
    credential: TSystemCredential;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCredentialAddingFor(null);
    }
  }, [isOpen]);

  const {
    configName,
    setConfigName,
    selectedCredentials,
    localCredentialOptions,
    handleCredentialChange,
    handleSubmit,
    handleClose,
    getConfigNameError,
    isFormValid,
  } = useCreateNewConfigModal({
    onClose,
    agent,
    agentConfigs,
    credentialOptions,
    onSubmit,
  });

  const handleInlineCredentialSubmit = async (
    slotKey: string,
    credential: TSystemCredential,
    data: TCredentialCreate
  ) => {
    const result = await onCreateCredential(credential, data);
    handleCredentialChange(slotKey, result.referenceId);
    setCredentialAddingFor(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create new Config"
      placement="center"
      maxWidth="600px"
      footer={
        <HStack gap={3} justifyContent="flex-end" w="100%">
          <Button
            onClick={handleClose}
            variant="outline"
            fontSize={14}
            px={4}
            py={2.5}
            fontWeight={600}
            color="purple.fg"
            borderColor="purple.fg"
            _hover={{
              backgroundColor: 'purple.50',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
            backgroundColor="purple.fg"
            color="white"
            fontSize={14}
            px={4}
            py={2.5}
            fontWeight={600}
            borderRadius={4}
            border="1px solid"
            borderColor="purple.muted"
            _hover={{
              backgroundColor: 'purple.600',
            }}
            loading={isLoading}
            loadingText="Creating..."
          >
            Create Config
          </Button>
        </HStack>
      }
    >
      <VStack gap={6} align="stretch" mb={6}>
        <Text fontSize={14} color="fg.muted">
          Name the Config and choose which credentials it should contain.
        </Text>

        <CustomTextField
          value={configName}
          onChange={setConfigName}
          placeholder="e.g. Customer C · Default Config"
          label="Config name"
          required
          errorText={getConfigNameError()}
        />

        <Box>
          <Text fontSize={18} fontWeight={500} color="fg" mb={4}>
            Credentials to include
          </Text>
          <VStack align="stretch" gap={4}>
            {agent.credentials.map((credential, slotIndex) => {
              const slotKey = `${agent.referenceId}:${slotIndex}`;
              const credentialSelectOptions = localCredentialOptions[credential.name] || [];

              return (
                <Box key={slotKey} pb={4}>
                  <HStack mb={3}>
                    {/* TODO: Add credential icon based on credential.type */}
                    <Text fontSize={16} fontWeight={500} color="fg">
                      {credential.name}
                    </Text>
                  </HStack>
                  <Text fontSize={14} color="fg.muted" mb={3}>
                    Use Credentials
                  </Text>
                  <SelectWithNewOption
                    options={credentialSelectOptions}
                    value={selectedCredentials[slotKey]}
                    onChange={(value) => handleCredentialChange(slotKey, value)}
                    placeholder="Company API key"
                    onAddNew={() => setCredentialAddingFor({ slotKey, credential })}
                    addNewButtonText="Add new Credential"
                  />
                  {credentialAddingFor?.slotKey === slotKey && (
                    <Box mt={4} pl={0}>
                      <Text fontSize={16} fontWeight={500} color="fg" mb={3}>
                        New Credential
                      </Text>
                      <CreateCredentialForm
                        credential={credential}
                        onSubmit={(data) => handleInlineCredentialSubmit(slotKey, credential, data)}
                        onCancel={() => setCredentialAddingFor(null)}
                        isLoading={isCreatingCredential}
                        submitLabel="Save"
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </VStack>
        </Box>
      </VStack>
    </Modal>
  );
};

export default CreateNewConfigModal;

